import React, { useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SESSION_TIMEOUT_CONFIG } from '../../../constants/sessionTimeout';
import { NotificationContext } from '../../../contexts/NotificationsContext';
import userAuth from '../../../hooks/userAuth';
import useSessionTimeout from '../../../hooks/useSessionTimeout';
import './SessionTimeoutProviderConfig';

interface ISessionTimeoutProviderProps {
  children: React.ReactNode;
  isEnabled?: boolean;
}

const SessionTimeoutProvider = ({ children, isEnabled = true }: ISessionTimeoutProviderProps): JSX.Element => {
  const { t } = useTranslation('SessionTimeoutProvider');
  const { logout, auth } = userAuth();
  const isUserLoggedIn = Boolean(auth);
  const isSessionTimeoutEnabled = isEnabled && isUserLoggedIn;

  const { notificationOpen, setShowNotification, deleteNotification } = useContext(NotificationContext);
  const [warningShown, setWarningShown] = useState(false);

  const handleTimeout = useCallback(async () => {
    deleteNotification(SESSION_TIMEOUT_CONFIG.NOTIFICATION_ID);
    setWarningShown(false);
    setShowNotification(false);
    await logout();
    window.location.href = '/auth/login';
  }, [logout, setWarningShown, setShowNotification, deleteNotification]);

  const handleWarning = useCallback(() => {
    if (!isUserLoggedIn) {
      setShowNotification(false);
      setWarningShown(false);

      return;
    }

    if (!warningShown) {
      setWarningShown(true);
      notificationOpen({
        listNotifications: [
          {
            id: SESSION_TIMEOUT_CONFIG.NOTIFICATION_ID,
            title: t('SESSION_TIMEOUT_WARNING_TITLE'),
            description: (
              <div>
                <div>{t('SESSION_TIMEOUT_WARNING_DESCRIPTION')}</div>
              </div>
            ),
            severity: 'warning',
            autoClose: true,
            autoHideDuration: SESSION_TIMEOUT_CONFIG.DEFAULT_TIMEOUT_MINUTES * 60 * 1000,
          },
        ],
      });
      setShowNotification(true);
    }
  }, [warningShown, notificationOpen, setShowNotification, t, isUserLoggedIn]);

  const resetSession = useCallback(() => {
    setWarningShown(false);
    setShowNotification(false);
  }, [setShowNotification]);

  useSessionTimeout({
    onTimeout: handleTimeout,
    onWarning: handleWarning,
    onActive: resetSession,
    timeoutMinutes: SESSION_TIMEOUT_CONFIG.DEFAULT_TIMEOUT_MINUTES,
    warningMinutes: SESSION_TIMEOUT_CONFIG.DEFAULT_WARNING_MINUTES,
    isEnabled: isSessionTimeoutEnabled,
  });

  return <>{children}</>;
};

export default SessionTimeoutProvider;
